import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  END_SUPPORT_ACCESS,
  GET_MY_SUPPORT_ACCESS_REQUESTS,
  Modal,
  RESPOND_TO_SUPPORT_ACCESS_REQUEST,
} from '@employ-engage/common-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useSupportSession,
  useVisiblePollInterval,
} from './use-support-session';

// The user's app has no push channel, so it checks for requests on an
// interval. A request stays answerable for 10 minutes, so this only sets how
// long the admin waits, and every signed-in tab pays for it.
const POLL_INTERVAL_MS = 30 * 1000;

type SupportAccessRequest = {
  id: string;
  requesterEmail: string;
  reason?: string | null;
  status: string;
  accessExpiresAt?: string | null;
};

export const formatTime = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

/**
 * Asks the signed-in user to allow or deny a super admin's request to act as
 * them, and — once allowed — shows how long access lasts with a way to end it.
 */
export const SupportAccessPrompt = () => {
  const { t } = useTranslation(['common']);
  const { supportSession, loading: sessionLoading } = useSupportSession();
  const [error, setError] = useState<string | null>(null);

  const { data, refetch } = useQuery(GET_MY_SUPPORT_ACCESS_REQUESTS, {
    pollInterval: useVisiblePollInterval(POLL_INTERVAL_MS),
    fetchPolicy: 'network-only',
    // A super admin acting as this user must never see or answer these.
    skip: sessionLoading || !!supportSession,
  });
  const [respond, { loading: responding }] = useMutation(
    RESPOND_TO_SUPPORT_ACCESS_REQUEST
  );
  const [endAccess, { loading: ending }] = useMutation(END_SUPPORT_ACCESS);

  if (supportSession) return null;

  const requests: SupportAccessRequest[] = data?.mySupportAccessRequests ?? [];
  const pending = requests.find((request) => request.status === 'pending');
  const approved = requests.find((request) => request.status === 'approved');

  const answer = async (approve: boolean) => {
    if (!pending) return;
    setError(null);
    try {
      await respond({ variables: { id: pending.id, approve } });
    } catch {
      setError(t('This request has expired or was already answered.'));
    }
    await refetch();
  };

  const end = async () => {
    if (!approved) return;
    await endAccess({ variables: { id: approved.id } });
    await refetch();
  };

  return (
    <>
      <Modal
        open={!!pending}
        setOpen={() => undefined}
        disableCloseOnOutsideClick
        panelClassName="relative w-full sm:max-w-lg"
      >
        {pending && (
          <div>
            <h2
              id="support-access-title"
              className="text-lg font-semibold text-gray-900"
            >
              {t('Support access request')}
            </h2>
            <p className="mt-3 text-sm text-gray-700">
              {t('support-access-request-body', {
                email: pending.requesterEmail,
              })}
            </p>
            {pending.reason && (
              <blockquote className="mt-3 border-l-4 border-gray-300 pl-3 text-sm italic text-gray-700">
                {pending.reason}
              </blockquote>
            )}
            <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-gray-700">
              <li>{t('support-access-can-do')}</li>
              <li>{t('support-access-cannot-do')}</li>
              <li>{t('support-access-can-end')}</li>
            </ul>
            {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
            <div className="mt-6 flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={responding}
                onClick={() => answer(false)}
              >
                {t('Deny')}
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={responding}
                onClick={() => answer(true)}
              >
                {t('Allow access')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {approved && !pending && (
        <div
          role="status"
          className="fixed inset-x-0 bottom-4 z-40 mx-auto flex w-fit max-w-[90vw] items-center gap-3 rounded-full bg-amber-100 px-4 py-2 text-sm text-gray-900 shadow-lg ring-1 ring-amber-300"
        >
          <span>
            {t('support-access-active', {
              email: approved.requesterEmail,
              time: formatTime(approved.accessExpiresAt),
            })}
          </span>
          <button
            type="button"
            disabled={ending}
            onClick={end}
            className="rounded-full bg-gray-900 px-3 py-1 text-xs font-semibold text-white hover:bg-gray-700"
          >
            {t('End access')}
          </button>
        </div>
      )}
    </>
  );
};

export default SupportAccessPrompt;
